import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { History } from 'history';
import styled, { css } from 'styled-components';
import { useSelector } from 'react-redux';
import isRoutePathExact from '../utils/isRoutePathExact';
import { RootState } from '../redux/root';

const SidebarSubMenuItem = ({
  to,
  title,
  basePath,
}: Readonly<Props>): React.ReactElement => {
  const { pathname } = useLocation();
  const { push }: History = useHistory();
  const activityCounts = useSelector(
    (s: RootState) => s.employeeActivities,
  );

  const handleClick = () => {
    if (basePath) {
      return push(`${basePath}/${to}`);
    }
    return push(to);
  };

  const isActive = isRoutePathExact(
    pathname,
    basePath ? `${basePath}/${to}` : to,
  );

  return (
    <Tab
      type="button"
      onClick={handleClick}
      $active={isActive}
      role="tab"
      aria-selected={isActive}
    >
      <span>{title}</span>
      {isActive && activityCounts[title] > 0 && (
        <Count>{activityCounts[title]}</Count>
      )}
    </Tab>
  );
};

const Tab = styled.button<{ $active: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  height: 36px;
  padding: 0 var(--space-4);
  border: 0;
  background: transparent;
  border-radius: var(--radius-md);
  color: var(--neutral-600);
  font-family: inherit;
  font-size: var(--fs-base);
  font-weight: var(--fw-medium);
  cursor: pointer;
  white-space: nowrap;
  transition: background-color var(--motion-fast) var(--ease-standard),
    color var(--motion-fast) var(--ease-standard);

  /* The Settings tab row holds up to 13 tabs. Browser zoom shrinks the
     effective viewport width, so on narrower / zoomed-in widths tighten the
     horizontal padding to fit more tabs before horizontal scrolling kicks in.
     At 1440px and below this reclaims ~100px across the row. */
  @media only screen and (max-width: 1440px) {
    padding: 0 var(--space-3);
  }

  @media only screen and (max-width: 1180px) {
    padding: 0 var(--space-2);
  }

  &:hover {
    background: var(--neutral-100);
    color: var(--neutral-900);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  ${({ $active }) =>
    $active &&
    css`
      background: var(--primary-50);
      color: var(--primary-700);
      font-weight: var(--fw-semibold);
    `}
`;

const Count = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: var(--primary-600);
  color: var(--neutral-0);
  border-radius: var(--radius-full);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
`;

interface Props {
  to: string;
  title: string;
  basePath?: string;
}

export default SidebarSubMenuItem;
